/**
 * Ctrl+K: jump to any screen or any account.
 *
 * Screens come from the shared menu (so a user only finds what they may
 * open); accounts come from the in-memory ledger cache, so search never
 * calls the server while the user types (PROJECT.md decision 7). Picking
 * an account opens its ledger statement.
 *
 * Ctrl+K is caught on the window rather than through the hotkey stack: it
 * must work on every screen, including inside a field, whatever scope is on
 * top. While the palette is open it pushes its own scope, which suspends
 * the screen's shortcuts underneath.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Dialog, InputBase, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useLedgers } from '../store/LedgerCache'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import { useGuard } from '../store/UnsavedChanges'
import { modulesOf, visibleMenu, type MenuItem } from '../nav/menu'
import { c } from '../theme'
import MenuIcon from './MenuIcon'
import Keycap from './Keycap'

interface Result {
  id: string
  kind: 'screen' | 'account'
  label: string
  meta: string
  path: string
  item?: MenuItem
}

/** Opens on Ctrl+K (Cmd+K on a Mac), whatever the keyboard layout. */
export function isPaletteKey(e: KeyboardEvent): boolean {
  return (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey &&
    (e.code === 'KeyK' || e.key.toLowerCase() === 'k')
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!isPaletteKey(e)) return
      e.preventDefault()
      setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  return { open, setOpen }
}

export default function CommandPalette(
  { open, onClose }: { open: boolean; onClose: () => void },
) {
  const { t, i18n } = useTranslation()
  const { can, me } = useAuth()
  const modules = useMemo(() => modulesOf(me), [me])
  const { search } = useLedgers()
  const nav = useNavigate()
  const guard = useGuard()
  const [q, setQ] = useState('')
  const [index, setIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  useHotkeys('palette', [
    { combo: 'escape', handler: onClose, allowInInput: true },
  ], open)

  useEffect(() => { if (open) { setQ(''); setIndex(0) } }, [open])

  const screens = useMemo(() => {
    const en = i18n.getFixedT('en')
    return visibleMenu(can, modules).flatMap((g) => g.items.map((item) => ({
      item,
      label: t(item.key),
      group: t(g.key),
      // Match the English name too, so "ledger" works with Arabic on.
      haystack: `${t(item.key)} ${en(item.key)}`.toLowerCase(),
    })))
  }, [can, modules, t, i18n])

  const results: Result[] = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const shownScreens = (needle
      ? screens.filter((s) => s.haystack.includes(needle))
      : screens.filter((s) => s.item.path === '/' || s.item.kind)
    ).slice(0, needle ? 8 : 7)
    const out: Result[] = shownScreens.map((s) => ({
      id: `s:${s.item.path}`, kind: 'screen', label: s.label, meta: s.group,
      path: s.item.path, item: s.item,
    }))
    // Accounts open the ledger statement, which needs operational reports.
    if (needle && can(P.reportOperational)) {
      for (const l of search(needle, 8)) {
        out.push({
          id: `a:${l.code}`, kind: 'account',
          label: (i18n.language === 'ar' && l.name_ar) || l.name_en,
          meta: l.code,
          path: `/ledger?code=${encodeURIComponent(l.code)}`,
        })
      }
    }
    return out
  }, [q, screens, search, can, i18n.language])

  useEffect(() => { setIndex(0) }, [q])

  const go = useCallback((r: Result | undefined) => {
    if (!r) return
    onClose()
    guard(() => nav(r.path))
  }, [nav, onClose, guard])

  function onKeyDown(e: ReactKeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      go(results[index])
    }
  }

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${index}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [index])

  const screenRows = results.filter((r) => r.kind === 'screen')
  const accountRows = results.filter((r) => r.kind === 'account')

  const row = (r: Result) => {
    const i = results.indexOf(r)
    const active = i === index
    return (
      <Box key={r.id} id={`palette-${i}`} data-index={i} role="option"
        aria-selected={active}
        onMouseEnter={() => setIndex(i)}
        onMouseDown={(e) => { e.preventDefault(); go(r) }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, minHeight: 40,
          borderRadius: '6px', cursor: 'pointer',
          bgcolor: active ? c.primarySoft : 'transparent',
          color: active ? c.primaryInk : c.ink,
        }}>
        {r.item
          ? <MenuIcon name={r.item.icon} />
          : <Box aria-hidden sx={{ width: 18, textAlign: 'center', fontSize: 11,
                                   color: c.inkMuted }}>#</Box>}
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: active ? 600 : 400,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {r.label}
        </Typography>
        <Typography className={r.kind === 'account' ? 'doc-no' : undefined}
          sx={{ fontSize: 12, color: c.inkMuted, whiteSpace: 'nowrap' }}>
          {r.meta}
        </Typography>
        {r.item?.combo && <Keycap combo={r.item.combo} />}
      </Box>
    )
  }

  const heading = (label: string) => (
    <Typography component="div" sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: c.inkMuted, px: 1.5, pt: 1.25, pb: 0.5 }}>
      {label}
    </Typography>
  )

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      aria-label={t('shell.search')}
      PaperProps={{ sx: { position: 'fixed', top: { xs: 16, sm: '12vh' }, m: { xs: 1, sm: 2 },
                          border: `1px solid ${c.borderStrong}` } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2,
                 borderBottom: `1px solid ${c.border}` }}>
        <SearchIcon sx={{ color: c.inkMuted }} aria-hidden />
        <InputBase autoFocus fullWidth value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('shell.searchPlaceholder')}
          inputProps={{
            role: 'combobox', 'aria-expanded': true, 'aria-controls': 'palette-list',
            'aria-activedescendant': results.length ? `palette-${index}` : undefined,
            'aria-label': t('shell.search'),
          }}
          sx={{ py: 1.5, fontSize: 16, '& input': { boxShadow: 'none' } }} />
        <Keycap combo="escape" />
      </Box>
      <Box id="palette-list" ref={listRef} role="listbox" aria-label={t('shell.results')}
        sx={{ maxHeight: 420, overflowY: 'auto', p: 1 }}>
        {screenRows.length > 0 && heading(t('shell.screens'))}
        {screenRows.map(row)}
        {accountRows.length > 0 && heading(t('shell.accounts'))}
        {accountRows.map(row)}
        {results.length === 0 && (
          <Typography sx={{ px: 1.5, py: 2, color: c.inkMuted, fontSize: 14 }}>
            {t('shell.noResults', { q })}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2, px: 2, py: 1,
                 borderTop: `1px solid ${c.border}`, fontSize: 12, color: c.inkMuted }}>
        <span>↑↓ {t('shell.move')}</span>
        <span>Enter {t('shell.open')}</span>
      </Box>
    </Dialog>
  )
}
